import React from 'react';
import {
  Shield,
  EyeOff,
  Server,
  Mail,
  Lock,
  Database,
} from 'lucide-react';

import {
  Anchor,
  Badge,
  Box,
  Container,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

function SectionTitle({ icon, title }) {
  return (
    <Group gap="sm" mt="xl" mb="md">
      <ThemeIcon variant="light" color="blue" radius="xl">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </Group>
  );
}

function LegalCard({ title, children }) {
  return (
    <Stack gap={4}>
      <Text fz="xs" fw={800} tt="uppercase" lts={1}>
        {title}
      </Text>
      {children}
    </Stack>
  );
}

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');

  return (
    <Box mih="100vh" py={60}>
      <Container size="md">
        <Stack gap="lg">
          <Paper
            withBorder
            radius="xl"
            p="xl"
            bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}
          >
            <Stack align="center" ta="center" gap="md">
              <Title order={1} fz="2.5rem" fw={800}>
                {t('Privacy Policy')}
              </Title>
              <Badge
                variant="light"
                color="blue"
                size="lg"
                radius="xl"
                leftSection={<Lock size={14} />}
              >
                {t('Effective Date: March 18, 2026')}
              </Badge>
            </Stack>
          </Paper>

          <Text>
            {t('At Timestamp GIT, operated by')}{' '}
            <Text component="span" fw={700}>
              RSJ Software GmbH
            </Text>{' '}
            {t('("we", "our", or "us"), privacy and security are the foundational principles of our architecture. We provide a zero-knowledge cryptographic timestamping service designed to prove the existence of your intellectual property without ever requiring access to the intellectual property itself.')}
          </Text>
          <Text>
            {t('This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our SaaS platform, interact with our GitHub App, or utilize our Enterprise Docker containers (collectively, the "Services").')}
          </Text>

          <Paper withBorder radius="lg" p="xl" bg={colorScheme === 'dark' ? 'dark.6' : 'teal.0'}>
            <Stack gap="md">
              <Group gap="sm">
                <ThemeIcon variant="light" color="teal" radius="xl">
                  <EyeOff size={16} />
                </ThemeIcon>
                <Title order={2} c="teal">
                  {t('1. Our Zero-Knowledge Guarantee')}
                </Title>
              </Group>

              <Text>
                {t('Before detailing standard data collection practices, we want to explicitly state our core security guarantee regarding your source code:')}
              </Text>

              <Paper withBorder p="md" radius="md">
                <Text fw={700} fz="lg" c="teal" ta="center">
                  {t('We do not read, copy, store, or transmit your actual source code.')}
                </Text>
              </Paper>

              <Text>
                {t('Whether you use our Standard Mode (GitHub App) or Enterprise ZK Mode (GitHub Actions/Docker), our systems only extract, receive, and process cryptographic hashes (e.g., SHA-256) of your Git commits.')}
              </Text>
            </Stack>
          </Paper>

          <SectionTitle icon={<Database size={16} />} title={t('2. Information We Collect')} />
          <Text>
            {t('While we do not collect your source code, we do collect standard business and metadata necessary to provide and secure our Services:')}
          </Text>

          <Title order={3} fz="lg" fw={600}>
            {t('A. Information You Provide to Us')}
          </Title>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Account Information:')}{' '}
              </Text>
              {t('Name, email address, company name, and password when you register for an account.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Billing Information:')}{' '}
              </Text>
              {t('Credit card details and billing address (processed securely via our third-party payment processors like Stripe; we do not store full credit card numbers).')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Support Data:')}{' '}
              </Text>
              {t('Information you provide when contacting our customer support team.')}
            </List.Item>
          </List>

          <Title order={3} fz="lg" fw={600}>
            {t('B. Data Collected via Our Services')}
          </Title>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Cryptographic Hashes:')}{' '}
              </Text>
              {t('The SHA-256 (or similar) hashes of your Git commits.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Repository Metadata:')}{' '}
              </Text>
              {t('Repository names, commit timestamps, and standard Git author metadata.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Shadow Repository Configuration:')}{' '}
              </Text>
              {t('Credentials or OAuth tokens granting us the explicit, scoped Write Access required to push ')}
              <CodeInline>.ots</CodeInline>
              {t(' proofs and audit ledgers to your designated Shadow Repositories.')}
            </List.Item>
          </List>

          <Title order={3} fz="lg" fw={600}>
            {t('C. Automatically Collected Information')}
          </Title>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Log and Usage Data:')}{' '}
              </Text>
              {t('IP addresses, browser types, operating systems, referring URLs, and interaction data.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Cookies and Tracking:')}{' '}
              </Text>
              {t('Standard cookies to maintain user sessions and analyze site traffic.')}
            </List.Item>
          </List>

          <SectionTitle icon={<Shield size={16} />} title={t('3. How We Use Your Information')} />
          <Text>{t('We use the collected information for the following purposes:')}</Text>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('To Provide the Service:')}{' '}
              </Text>
              {t('Generating Merkle trees and anchoring hashes to the Bitcoin blockchain.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('To Deliver Proofs:')}{' '}
              </Text>
              {t('Pushing ')}
              <CodeInline>.ots</CodeInline>
              {t(' receipts and daily audit ledgers to your Shadow Repository.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('To Manage Your Account:')}{' '}
              </Text>
              {t('Processing payments and providing customer support.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('To Improve Our Platform:')}{' '}
              </Text>
              {t('Analyzing usage trends to optimize our dashboard and Docker images.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('To Communicate:')}{' '}
              </Text>
              {t('Sending service updates and security alerts.')}
            </List.Item>
          </List>

          <SectionTitle icon={<Lock size={16} />} title={t('4. How We Share Your Information')} />
          <Text>
            {t('We do not sell your personal information. We may share your information only in the following specific circumstances:')}
          </Text>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('The Bitcoin Blockchain:')}{' '}
              </Text>
              {t('Merkle Roots containing the mathematical representation of your hashes are broadcast to the Bitcoin blockchain. ')}
              <Text component="span" fw={700}>
                {t('This data is public and immutable.')}
              </Text>
              {t(' Because it is a one-way hash, your code cannot be reverse-engineered.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Public Badges:')}{' '}
              </Text>
              {t('If you choose to embed verification badges, basic metadata will be accessible via our public verification endpoints.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Service Providers:')}{' '}
              </Text>
              {t('Trusted third-party vendors (e.g., AWS, Stripe).')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Legal Requirements:')}{' '}
              </Text>
              {t('If required to do so by law or court order.')}
            </List.Item>
          </List>

          <SectionTitle icon={<Database size={16} />} title={t('5. Data Retention and Deletion')} />
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Account Data:')}{' '}
              </Text>
              {t('Retained as long as your account is active or needed to comply with legal obligations.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Blockchain Immutability:')}{' '}
              </Text>
              {t('Once anchored, a hash ')}
              <Text component="span" fw={700}>
                {t('cannot be deleted, modified, or removed')}
              </Text>
              {t('. It is a permanent record.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Account Deletion:')}{' '}
              </Text>
              {t('Severing connections to GitHub and deleting personal metadata from our active databases.')}
            </List.Item>
          </List>

          <SectionTitle icon={<Shield size={16} />} title={t('6. Your Data Protection Rights')} />
          <Text>
            {t('Depending on your location (e.g., GDPR, CCPA), you may have rights regarding access, rectification, objection, and portability of your personal data.')}
          </Text>

          <SectionTitle icon={<Server size={16} />} title={t('7. Self-Hosted / Docker Environments')} />
          <Text>
            {t('For Enterprise customers utilizing our Self-Hosted Docker deployment:')}
          </Text>
          <List>
            <List.Item>{t('We do not collect telemetry or hashes from your internal instances.')}</List.Item>
            <List.Item>{t('Instances only communicate externally with public OpenTimestamps calendar servers and Bitcoin nodes.')}</List.Item>
          </List>

          <SectionTitle icon={<Mail size={16} />} title={t('8. Changes to This Privacy Policy')} />
          <Text>
            {t('We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page.')}
          </Text>

          <SectionTitle icon={<Mail size={16} />} title={t('9. Contact Us')} />
          <Text>
            {t('If you have any questions regarding this Privacy Policy, please contact our Data Protection Officer:')}
          </Text>

          <Paper radius="lg" p="lg" withBorder>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              <LegalCard title={t('Operator')}>
                <Text fz="sm">RSJ Software GmbH</Text>
                <Text fz="sm">Holzstrasse 4</Text>
                <Text fz="sm">82110 Germering, Germany</Text>
              </LegalCard>

              <LegalCard title={t('Contact')}>
                <Text fz="sm">Phone: +49 89 89 41 42-0</Text>
                <Text fz="sm">Fax: +49 89 89 41 42-80</Text>
                <Text fz="sm">
                  Email: <Anchor href="mailto:info@rsj.de">info@rsj.de</Anchor>
                </Text>
                <Text fz="sm">
                  Web: <Anchor href="http://www.rsj.de">www.rsj.de</Anchor>
                </Text>
              </LegalCard>

              <LegalCard title={t('Legal Representative')}>
                <Text fz="sm" c="dimmed">
                  {t('Managing Director:')}
                </Text>
                <Text fz="sm">Ruediger Jungbeck</Text>
              </LegalCard>

              <LegalCard title={t('Registry & Tax')}>
                <Text fz="sm">Trade Register: Munich HRB 76084</Text>
                <Text fz="sm">VAT-ID: DE 128 239 592</Text>
                <Text fz="sm">Tax ID: 117/136/80100</Text>
              </LegalCard>
            </SimpleGrid>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

function CodeInline({ children }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}
